import React, { useCallback, useEffect } from 'react';
import {
  FluentProvider,
  webLightTheme,
  makeStyles,
  mergeClasses,
  shorthands,
  tokens,
  Button,
  type Theme,
} from '@fluentui/react-components';
import { I18nProvider, useI18nContext } from '@bingads-webui/copilot-i18n';
import { type ILogger } from '@bingads-webui-react/copilot-utils';
import { COPILOT_WIDTH, fakeLogger } from './utils';
import { MSACopilotErrorIcon } from './msa-copilot-error-icon';

const useStyles = makeStyles({
  root: {
    ...shorthands.flex(1),
    backgroundColor: tokens.colorNeutralBackground3,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    borderLeftWidth: '1px',
    borderLeftStyle: 'solid',
    borderLeftColor: tokens.colorNeutralStroke1,
    height: '100%',
    width: `${COPILOT_WIDTH}px`,
  },
  errorIcon: {
    marginBottom: tokens.spacingVerticalL,
    '& svg': {
      height: '128px',
      width: '128px',
    },
  },
  errorTitleText: {
    color: tokens.colorNeutralForeground1,
    fontSize: tokens.fontSizeBase500,
    fontWeight: tokens.fontWeightSemibold,
    lineHeight: tokens.lineHeightBase500,
    marginTop: tokens.spacingVerticalNone,
    marginBottom: tokens.spacingVerticalS,
    textAlign: 'center',
  },
  errorDescriptionText: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightRegular,
    lineHeight: tokens.lineHeightBase200,
    marginBottom: tokens.spacingVerticalNone,
    textAlign: 'center',
  },
  refreshButton: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    lineHeight: tokens.lineHeightBase300,
    marginTop: tokens.spacingVerticalM,
  },
});

interface IMSACopilotErrorViewInternalProps {
  errorTitle?: string;
  errorDescription?: string;
  refreshButtonText?: string;
  onRefresh?: () => void;
}

const MSACopilotErrorViewInternal = (props: IMSACopilotErrorViewInternalProps) => {
  const {
    errorTitle, errorDescription, refreshButtonText, onRefresh,
  } = props;

  const i18n = useI18nContext();
  const styles = useStyles();

  const onRefreshHandler = useCallback(() => {
    if (!onRefresh) {
      window.location.reload();
    } else {
      onRefresh();
    }
  }, []);

  return (
    <div className={mergeClasses(styles.root, 'msa-copilot-error-view')}>
      <span className={mergeClasses(styles.errorIcon, 'msa-copilot-error-icon')}>
        <MSACopilotErrorIcon />
      </span>
      <h1 className={mergeClasses(styles.errorTitleText, 'msa-copilot-error-title')}>
        {errorTitle ?? i18n.getString(_TL_('There is some error here'))}
      </h1>
      <p className={mergeClasses(styles.errorDescriptionText, 'msa-copilot-error-description')}>
        {errorDescription ?? i18n.getString(_TL_("We've encountered an error and can't continue. Please refresh to start a new chat."))}
      </p>
      <Button className={mergeClasses(styles.refreshButton, 'msa-copilot-refresh-button')} onClick={onRefreshHandler}>
        {refreshButtonText ?? i18n.getString(_TL_('Refresh page'))}
      </Button>
    </div>
  );
};

export interface IMSACopilotErrorViewProps extends IMSACopilotErrorViewInternalProps {
  error: any;
  locale?: string;
  theme?: Theme;
  logger?: ILogger;
}

export const MSACopilotErrorView = (props: IMSACopilotErrorViewProps) => {
  const {
    error,
    locale = 'en-us',
    theme = webLightTheme,
    logger = fakeLogger,
    ...restProps
  } = props;

  useEffect(() => {
    if (error instanceof Error) {
      logger.error(`[Copilot - Error boundary] Copilot failed to render. Error message: ${error.message}, stack: ${error.stack}`);
    } else {
      logger.error(`[Copilot - Error boundary] Copilot failed to render. Error: ${JSON.stringify(error)}`);
    }
  }, [error, logger]);

  return (
    <FluentProvider theme={theme}>
      <I18nProvider locale={locale}>
        <MSACopilotErrorViewInternal {...restProps} />
      </I18nProvider>
    </FluentProvider>
  );
};
